/**
 * TypeScript interfaces for Structurizr workspace JSON format
 */

// ============= Model Elements =============

export interface Element {
  id: string
  name: string
  description?: string
  tags?: string
  properties?: Record<string, string>
  url?: string
}

export interface Person extends Element {
  location?: 'Internal' | 'External' | 'Unspecified'
  relationships?: Relationship[]
}

export interface SoftwareSystem extends Element {
  location?: 'Internal' | 'External' | 'Unspecified'
  containers?: Container[]
  relationships?: Relationship[]
}

export interface Container extends Element {
  technology?: string
  components?: Component[]
  relationships?: Relationship[]
}

export interface Component extends Element {
  technology?: string
  relationships?: Relationship[]
}

export interface DeploymentNode extends Element {
  environment: string
  technology?: string
  instances?: string
  children?: DeploymentNode[]
  infrastructureNodes?: InfrastructureNode[]
  softwareSystemInstances?: SoftwareSystemInstance[]
  containerInstances?: ContainerInstance[]
  relationships?: Relationship[]
}

export interface InfrastructureNode extends Element {
  technology?: string
  relationships?: Relationship[]
}

export interface SoftwareSystemInstance {
  id: string
  softwareSystemId: string
  instanceId: number
  environment: string
  properties?: Record<string, string>
  relationships?: Relationship[]
}

export interface ContainerInstance {
  id: string
  containerId: string
  instanceId: number
  environment: string
  properties?: Record<string, string>
  relationships?: Relationship[]
}

export interface Relationship {
  id: string
  sourceId: string
  destinationId: string
  description?: string
  technology?: string
  tags?: string
  properties?: Record<string, string>
  interactionStyle?: 'Synchronous' | 'Asynchronous'
  url?: string
  linkedRelationshipId?: string
}

export interface Model {
  enterprise?: {
    name: string
  }
  people?: Person[]
  softwareSystems?: SoftwareSystem[]
  deploymentNodes?: DeploymentNode[]
}

// ============= Views =============

export interface ElementView {
  id: string
  x?: number
  y?: number
}

export interface RelationshipView {
  id: string
  description?: string
  order?: string
  response?: boolean
  vertices?: Array<{ x: number; y: number }>
  routing?: 'Direct' | 'Curved' | 'Orthogonal'
}

export interface AutomaticLayout {
  implementation?: string
  rankDirection?: 'TopBottom' | 'BottomTop' | 'LeftRight' | 'RightLeft'
  rankSeparation?: number
  nodeSeparation?: number
  edgeSeparation?: number
  vertices?: boolean
}

export interface BaseView {
  key: string
  title?: string
  description?: string
  elements?: ElementView[]
  relationships?: RelationshipView[]
  automaticLayout?: AutomaticLayout
  paperSize?: string
  dimensions?: {
    width: number
    height: number
  }
}

export interface SystemLandscapeView extends BaseView {
  enterpriseBoundaryVisible?: boolean
}

export interface SystemContextView extends BaseView {
  softwareSystemId: string
  enterpriseBoundaryVisible?: boolean
}

export interface ContainerView extends BaseView {
  softwareSystemId: string
  externalSoftwareSystemBoundariesVisible?: boolean
}

export interface ComponentView extends BaseView {
  containerId: string
  externalSoftwareSystemBoundariesVisible?: boolean
}

export interface DynamicView extends BaseView {
  elementId?: string
  externalBoundariesVisible?: boolean
}

export interface DeploymentView extends BaseView {
  softwareSystemId?: string
  environment: string
}

export interface FilteredView extends BaseView {
  baseViewKey: string
  mode: 'Include' | 'Exclude'
  tags: string[]
}

export type View =
  | SystemLandscapeView
  | SystemContextView
  | ContainerView
  | ComponentView
  | DynamicView
  | DeploymentView
  | FilteredView

// ============= Styles =============

export interface ElementStyle {
  tag: string
  width?: number
  height?: number
  background?: string
  stroke?: string
  strokeWidth?: number
  color?: string
  fontSize?: number
  shape?: 'Box' | 'RoundedBox' | 'Circle' | 'Ellipse' | 'Hexagon' | 'Cylinder' | 'Pipe' | 'Person' | 'Robot' | 'Folder' | 'WebBrowser' | 'MobileDevicePortrait' | 'MobileDeviceLandscape' | 'Component'
  icon?: string
  border?: 'Solid' | 'Dashed' | 'Dotted'
  opacity?: number
  metadata?: boolean
  description?: boolean
  properties?: Record<string, string>
}

export interface RelationshipStyle {
  tag: string
  thickness?: number
  color?: string
  fontSize?: number
  width?: number
  dashed?: boolean
  routing?: 'Direct' | 'Curved' | 'Orthogonal'
  position?: number
  opacity?: number
  properties?: Record<string, string>
}

export interface Configuration {
  styles?: {
    elements?: ElementStyle[]
    relationships?: RelationshipStyle[]
  }
  themes?: string[]
  branding?: {
    logo?: string
    font?: {
      name?: string
      url?: string
    }
  }
  terminology?: {
    enterprise?: string
    person?: string
    softwareSystem?: string
    container?: string
    component?: string
    deploymentNode?: string
    infrastructureNode?: string
    relationship?: string
  }
}

export interface ViewSet {
  systemLandscapeViews?: SystemLandscapeView[]
  systemContextViews?: SystemContextView[]
  containerViews?: ContainerView[]
  componentViews?: ComponentView[]
  dynamicViews?: DynamicView[]
  deploymentViews?: DeploymentView[]
  filteredViews?: FilteredView[]
  configuration?: Configuration
}

// ============= Workspace =============

export interface StructurizrWorkspace {
  id?: number
  name: string
  description?: string
  version?: string
  revision?: number
  lastModifiedDate?: string
  lastModifiedUser?: string
  lastModifiedAgent?: string
  model: Model
  views?: ViewSet
  documentation?: Documentation
  properties?: Record<string, string>
  configuration?: WorkspaceConfiguration
}

export interface Documentation {
  sections?: DocumentationSection[]
  decisions?: Decision[]
  images?: Image[]
}

export interface DocumentationSection {
  elementId?: string
  title?: string
  content: string
  format?: 'Markdown' | 'AsciiDoc'
  order?: number
}

export interface Decision {
  id: string
  date?: string
  status?: string
  title: string
  content?: string
  format?: 'Markdown' | 'AsciiDoc'
  elementId?: string
  links?: DecisionLink[]
}

export interface DecisionLink {
  id: string
  description?: string
}

export interface Image {
  name: string
  content: string
  type: string
}

export interface WorkspaceConfiguration {
  users?: User[]
}

export interface User {
  username: string
  role: 'ReadWrite' | 'ReadOnly'
}

// ============= View Type Helpers =============

export type ViewType =
  | 'SystemLandscape'
  | 'SystemContext'
  | 'Container'
  | 'Component'
  | 'Dynamic'
  | 'Deployment'
  | 'Filtered'

export interface ViewInfo {
  key: string
  title: string
  type: ViewType
  view: View
}

export function getViewType(view: View): ViewType {
  if ('softwareSystemId' in view && 'environment' in view) return 'Deployment'
  if ('containerId' in view) return 'Component'
  if ('baseViewKey' in view) return 'Filtered'
  if ('softwareSystemId' in view && !('containerId' in view) && !('environment' in view)) {
    // Could be SystemContext or Container
    if ('externalSoftwareSystemBoundariesVisible' in view) return 'Container'
    return 'SystemContext'
  }
  if ('elementId' in view) return 'Dynamic'
  return 'SystemLandscape'
}

export function getAllViews(viewSet: ViewSet | undefined): ViewInfo[] {
  if (!viewSet) return []

  const views: ViewInfo[] = []

  viewSet.systemLandscapeViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'SystemLandscape', view: v })
  })

  viewSet.systemContextViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'SystemContext', view: v })
  })

  viewSet.containerViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'Container', view: v })
  })

  viewSet.componentViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'Component', view: v })
  })

  viewSet.dynamicViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'Dynamic', view: v })
  })

  viewSet.deploymentViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'Deployment', view: v })
  })

  viewSet.filteredViews?.forEach(v => {
    views.push({ key: v.key, title: v.title || v.key, type: 'Filtered', view: v })
  })

  return views
}
